/* global window */
/* global document */
import React, { Component } from 'react';
import moment from 'moment';
import { defineMessages, injectIntl } from 'react-intl';
import Highlight from 'react-highlighter';

import { getAuthToken } from 'actions/api';

const messages = defineMessages({
  EMPTY_MESSAGE: {
    id: 'BillingDownloadList.EMPTY_MESSAGE',
    defaultMessage: 'Es liegen noch keine Einzelverbindungsnachweise zum Download bereit. Die Verbindungen des aktuellen Monats können Sie unten einsehen.',
  },
  MONTH: {
    id: 'BillingDownloadList.MONTH',
    defaultMessage: 'Monat',
  },
  PDF: {
    id: 'BillingDownloadList.PDF',
    defaultMessage: 'PDF',
  },
});

class BillingDownloadList extends Component {
  constructor(props) {
    super(props);
    const savedState = localStorage.getItem('BillingDownloadListTableState');
    const defaultState = {
      sortColumn: 'month',
      sortDirection: 'desc',
      columnWidths: {
        col0: 250, // Monat
        col1: 150, // PDF
      }
    };
    
    this.state = savedState ? { ...defaultState, ...JSON.parse(savedState) } : defaultState;
    this.resizingColumn = null;
    this.startX = 0;
    this.startWidth = 0;
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  saveState = () => {
    const { sortColumn, sortDirection, columnWidths } = this.state;
    localStorage.setItem('BillingDownloadListTableState', JSON.stringify({ sortColumn, sortDirection, columnWidths }));
  }

  toggleSort = (column) => {
    this.setState(prevState => {
      let nextDir = 'asc';
      if (prevState.sortColumn === column) {
        nextDir = prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      return {
        sortColumn: column,
        sortDirection: nextDir
      };
    }, this.saveState);
  }

  handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    this.resizingColumn = columnKey;
    this.startX = e.pageX;
    this.startWidth = this.state.columnWidths[columnKey];
    
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    if (!this.resizingColumn) return;
    const diff = e.pageX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff);
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [this.resizingColumn]: newWidth
      }
    }));
  };

  handleResizeEnd = () => {
    this.resizingColumn = null;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.saveState();
  };

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <i className="fa fa-sort" style={{ marginLeft: '8px', color: 'var(--text-muted)', opacity: 0.3 }} />;
    }
    return <i className={`fa ${sortDirection === 'asc' ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc'}`} style={{ marginLeft: '8px', color: 'var(--text-color)' }} />;
  }

  render() {
    const { downloads, companyId, filter, intl: { formatMessage } } = this.props;
    const token = `Bearer ${getAuthToken()}`;

    // Filter to only include the last 12 months
    const twelveMonthsAgo = moment().subtract(12, 'months').startOf('month');
    let filteredItems = downloads ? downloads.filter(d => moment(d.month).isSameOrAfter(twelveMonthsAgo)) : [];

    if (filter) {
      const lowerFilter = filter.toLowerCase();
      filteredItems = filteredItems.filter(d => {
        const monthStr = moment(d.month).format('MMMM YYYY').toLowerCase();
        return monthStr.includes(lowerFilter);
      });
    }

    if (filteredItems.length === 0) {
      return (
        <div style={{ padding: '16px', backgroundColor: 'var(--hover-bg, rgba(0, 0, 0, 0.02))', borderLeft: '4px solid var(--primary-color, #3b82f6)', borderRadius: '8px', color: 'var(--text-color)', fontSize: '14px', lineHeight: '1.5', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <i className="fa fa-info-circle" style={{ color: 'var(--primary-color, #3b82f6)', fontSize: '18px', marginTop: '2px', flexShrink: 0 }} />
          <div>{formatMessage(messages.EMPTY_MESSAGE)}</div>
        </div>
      );
    }

    const { sortColumn, sortDirection, columnWidths } = this.state;

    const sortedItems = [...filteredItems].sort((a, b) => {
      if (!sortColumn) return 0;
      
      let valA, valB;
      if (sortColumn === 'month') {
        valA = moment(a.month).valueOf();
        valB = moment(b.month).valueOf();
      }
      
      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return sortDirection === 'asc' ? 1 : -1;
      if (valB === undefined || valB === null) return sortDirection === 'asc' ? -1 : 1;
      
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      return sortDirection === 'asc' ? 1 : -1;
    });

    const thBaseStyle = {
      padding: '16px 24px',
      color: 'var(--text-muted)',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none'
    };

    const getThStyle = (key) => ({
      ...thBaseStyle,
      width: `${columnWidths[key]}px`,
      minWidth: `${columnWidths[key]}px`,
      maxWidth: `${columnWidths[key]}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    });

    const resizerStyle = {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    };

    const renderResizer = (key) => (
      <div 
        style={resizerStyle} 
        onMouseDown={(e) => this.handleResizeStart(e, key)}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--text-muted)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    );

    return (
      <div style={{ width: '100%', overflowX: 'auto', backgroundColor: 'var(--panel-bg)', borderRadius: '8px', border: '1px solid var(--panel-border)' }}>
        <table style={{ minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--panel-bg)', borderBottom: '1px solid var(--panel-border)' }}>
              <th onClick={() => this.toggleSort('month')} style={getThStyle('col0')}>
                {formatMessage(messages.MONTH)}
                {this.renderSortIcon('month')}
                {renderResizer('col0')}
              </th>
              <th style={{ ...thBaseStyle, width: 'auto' }}>
                {formatMessage(messages.PDF)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(download => {
              const monthStr = moment(download.month).format('MMMM YYYY');
              return (
                <tr key={download.month} style={{ borderBottom: '1px solid var(--panel-border)', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--btn-default-hover-bg)' } }}>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter ? <Highlight search={filter}>{monthStr}</Highlight> : monthStr}
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <a
                      style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                      onClick={() => {
                        document.cookie = `Authorization=${token}; path=/api/`;
                        window.location = `/api/company/${companyId}/billing/${download.id}`;
                      }}
                    >
                      <i className="fa fa-file-pdf-o" /> {formatMessage(messages.PDF)}
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(BillingDownloadList);
